/**
 * SensitiveFieldInput — secure input for Category A fields.
 *
 * P13 Phase 3, session 27. Spec: docs/living/sensitive-data-export-spec.md v2.2
 *
 * Replaces the standard text/password input for Category A fields (tagged via
 * isCategoryAField() from data-classification.ts).
 *
 * Behaviour:
 *  - Masked by default via CSS (`-webkit-text-security`) on a `type="text"` input —
 *    NOT `type="password"` — with a show/hide toggle. WHY NOT type="password" (s112 SF-1):
 *    browser password managers autofill a saved login credential into every
 *    `type="password"` input on the page (they ignore `autoComplete="off"`), which
 *    polluted no-file Cat A fields with the login password and produced a masked-value +
 *    "Set up your Secure File" contradiction. A `type="text"` field masked by CSS gives the
 *    same visual masking with no password field for a manager to target. Belt-and-suspenders
 *    opt-out attributes (`data-1p-ignore`/`data-lpignore`/`data-bwignore`/`data-form-type`)
 *    cover third-party managers that also fill text fields.
 *    WHY masked: field values are typed from a physical source (card, authenticator
 *    app, wallet recovery sheet) and never need to be read back from the screen
 *    normally. The show toggle handles "verify I typed correctly." This is
 *    intentionally different from the passphrase-entry field in
 *    SecureFileOnboardingStep, which is show-by-default so the planner can write
 *    it accurately on a card.
 *  - Lock icon + "never on our servers" inline message.
 *  - On focus: if setup not yet complete, fires the SecureFileSignpost dialog (s111
 *    amendment) — it directs the planner to the Protect Your Access page to create the
 *    file, rather than creating it inline. Value already typed is preserved across it.
 *    (On the Protect Your Access page itself, suppressOnboardingPrompt disables this.)
 *  - On blur: captures value to SensitiveDataSessionStore (in-memory only —
 *    never localStorage / sessionStorage / IndexedDB / network).
 *  - Chrome/Edge: debounced (500ms) auto-save via saveToHandle().
 *    First save → FirstSaveMilestoneCard. Subsequent saves → AutoSaveToast.
 *    Handle failure → stale-handle toast; values remain dirty.
 *  - Safari/Firefox: dirty values tracked in session store only. Q4a banner
 *    (Phase 4) drives manual download flow.
 */

import React, { useState, useRef, useCallback, useEffect } from "react";
import { Lock, Eye, EyeOff, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useSensitiveDataSession } from "@/lib/sensitive-data-session-provider";
import { detectBrowserCategory } from "@/lib/file-system-access-detector";
import { buildContainer, saveToHandle } from "@/lib/secure-file";
import { SecureFileSignpost } from "@/components/SecureFileSignpost";
import { useSecureFileOpen } from "@/components/SecureFileOpenProvider";
import type { GenericField } from "@shared/schema";
import { SAGE } from "@shared/brand";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SensitiveFieldInputProps {
  field: Pick<
    GenericField,
    "id" | "name" | "fieldKey" | "fieldType"
  >;
  /** Server value prop. s88 (SDE Phase 1): IGNORED for display — Cat A values are read
   *  from the in-memory store only, never from the server (legacy plaintext is hidden).
   *  Kept in the signature for call-site compatibility. */
  value: string;
  /** s88: NOT called for the value path anymore (Cat A never saves to the server). Kept
   *  for signature compatibility with the generic field-input contract. */
  onChange: (v: string) => void;
  onBlur: () => void;
  readOnly?: boolean;
  clientId: string;
  /** Row id for table/expandable rows; null/undefined for single-record card fields.
   *  Combined with field.id into the store key so the same template field doesn't collide
   *  across rows (s88). */
  rowId?: string | null;
  /** From clientData.secureFileSetupAt — null means first-time user on any device. */
  secureFileSetupAt: Date | null;
  /** "table" suppresses card chrome (privacy message) for inline table-cell use. */
  variant?: "card" | "table";
  /** s111 Build 6 polish: suppress the auto-onboarding popup on focus. The Protect Your
   *  Access page drives secure-file setup inline, so it opts out of the per-field popup.
   *  Default false → the popup fires on focus as before (Gov IDs, Online Presence, etc.). */
  suppressOnboardingPrompt?: boolean;
  /** s111 Build 6 polish: render locked/disabled (grayed, no focus/interaction). Used to
   *  lock the two Cat A fields until the Secure File exists. Default false. */
  disabled?: boolean;
}

// CSS masking without type="password" (s112 SF-1). `-webkit-text-security: disc` renders
// the same bullets as a password field on Chromium, Safari, and Firefox (116+) while keeping
// the input `type="text"`, so browser password managers have no password field to autofill.
// Cast: `-webkit-text-security` isn't in React's CSSProperties typing.
const MASK_STYLE = { WebkitTextSecurity: "disc" } as unknown as React.CSSProperties;

// ---------------------------------------------------------------------------
// First-save milestone card
// ---------------------------------------------------------------------------

function FirstSaveMilestoneCard({
  fieldName,
  onDismiss,
}: {
  fieldName: string;
  onDismiss: () => void;
}) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onDismiss(); }}>
      <DialogContent
        className="max-w-md bg-[#FEFCF8] border border-[#D9D1C1]"
        data-testid="first-save-milestone-card"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-[#4F6B51]" />
            <DialogTitle className="font-['Fraunces'] font-medium text-[#2B2A26]">
              Your Secure Information File is set up
            </DialogTitle>
          </div>
        </DialogHeader>
        <DialogDescription asChild>
          <div className="space-y-3 text-sm text-[#2B2A26]">
            <p>
              Your <strong>{fieldName}</strong> was just saved to it. From now
              on, sensitive values save here automatically.
            </p>
            <p>Want to print your passphrase card now?</p>
          </div>
        </DialogDescription>
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              // TODO: PassphraseCardPrintView (Phase 5)
              console.log("TODO: PassphraseCardPrintView (Phase 5)");
              onDismiss();
            }}
          >
            Print passphrase card
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
          >
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// SensitiveFieldInput
// ---------------------------------------------------------------------------

export function SensitiveFieldInput({
  field,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  value: _serverValue, // s88: intentionally ignored — Cat A reads from the store only.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange: _onChange, // s88: intentionally unused — no server write path for Cat A.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onBlur: _onBlur, // s88: intentionally NOT called — Cat A never triggers a server save.
  readOnly,
  clientId,
  rowId,
  secureFileSetupAt,
  variant = "card",
  suppressOnboardingPrompt = false,
  disabled = false,
}: SensitiveFieldInputProps) {
  const [showValue, setShowValue] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [showMilestoneCard, setShowMilestoneCard] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // s88 (SDE Phase 1): the in-memory store key. field.id is the TEMPLATE field id, shared
  // across rows of a table/expandable section, so rowId is required to avoid collisions.
  const storeKey = `${field.id}::${rowId ?? "card"}`;
  // isFirstSaveRef: true until the first successful save in this session. Used
  // together with !secureFileSetupAt to decide milestone card vs toast — ensures
  // the card appears at most once per new-setup session even if the POST to
  // mark setup complete fails (non-fatal).
  const isFirstSaveRef = useRef(true);
  const session = useSensitiveDataSession();

  // sessionRef is updated every render so callbacks can always access the latest
  // session state without needing `session` in their useCallback dep arrays. This
  // prevents performSave / handleBlur / handleSetupComplete from being recreated
  // on every context update, keeping the component's hook count stable.
  const sessionRef = useRef(session);
  sessionRef.current = session;

  // s88: store-backed local input state. Seeded from the in-memory store ONLY — NEVER from
  // the server `value` prop, so a legacy plaintext value on the server is never shown. The
  // local copy diverges from the store while typing and is committed back on blur. Re-syncs
  // if the store value changes externally (Phase 3 .oesif open → loadValues).
  const storeValue = session.values[storeKey];
  const [localValue, setLocalValue] = useState<string>(() => storeValue ?? "");
  useEffect(() => {
    setLocalValue(storeValue ?? "");
  }, [storeValue]);

  const { toast } = useToast();
  const { openFile } = useSecureFileOpen();

  // Setup is considered complete for save purposes when either:
  // (a) the session store was initialised on this device (onboarding completed
  //     this session), or (b) the server record says setup was done before.
  const setupComplete = session.isSetupComplete || !!secureFileSetupAt;

  // Clear debounce timer on unmount to prevent calling setState on dead component.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const performSave = useCallback(async () => {
    const s = sessionRef.current;
    const browserCategory = detectBrowserCategory();

    if (browserCategory !== "chrome-edge") {
      // Safari/Firefox: values already captured in session store as dirty.
      // Q4a UnsavedSensitiveBanner (Phase 4) will surface the download prompt.
      console.log(
        "[SDE] Dirty values tracked for Safari/Firefox download path:",
        s.getDirtyFields()
      );
      return;
    }

    if (!s.fileHandle) {
      // No handle available — can happen if setup completed on Safari/Firefox
      // and the user is now on Chrome/Edge without re-running setup.
      // Graceful no-op: values stay dirty.
      return;
    }

    try {
      const bytes = await buildContainer({
        data: s.values,
        plaintext: "",
        passphrase: s.passphrase!,
      });
      await saveToHandle(s.fileHandle, bytes);

      // Mark all current session values as saved (clean).
      s.markSaved(Object.keys(s.values));

      // First save for a brand-new setup → milestone card.
      // !secureFileSetupAt: server hasn't recorded setup before this session,
      // meaning this is genuinely the planner's first save.
      if (isFirstSaveRef.current && !secureFileSetupAt) {
        isFirstSaveRef.current = false;
        setShowMilestoneCard(true);
      } else {
        isFirstSaveRef.current = false;
        toast({
          title: "Saved to your Secure Information File",
          duration: 3000,
        });
      }
    } catch {
      toast({
        title: "Couldn't save your Secure Information File",
        description:
          "Your file may have been moved or deleted. Values are held in memory until resolved.",
        variant: "destructive",
        duration: 10000,
      });
    }
  }, [secureFileSetupAt, toast]); // session accessed via sessionRef — no dep needed

  const handleFocus = useCallback(() => {
    // s111 Build 6: a page that drives setup inline opts out of the per-field popup.
    if (!setupComplete && !suppressOnboardingPrompt) {
      setOnboardingOpen(true);
    }
  }, [setupComplete, suppressOnboardingPrompt]);

  const handleBlur = useCallback(() => {
    // 🔒 FAIL-CLOSED (s88): commit the value to the in-memory store ONLY. We NEVER call the
    // parent's onBlur (the server-save trigger) for a Category A field. If the store write
    // throws, we still must not fall through to a server save — the value stays in the
    // controlled input for the user to retry; it never leaves the device.
    try {
      sessionRef.current.setValue(storeKey, localValue);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[SDE] Cat A store write failed — value held locally, never sent to server", err);
      return;
    }

    if (!setupComplete) return; // No file setup yet → value stays dirty in memory only.

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      performSave();
    }, 500);
  }, [storeKey, localValue, setupComplete, performSave]); // session accessed via ref

  // s111 amendment: the field-focus popup no longer creates the file inline — it signposts
  // to the Protect Your Access page (the single setup home). File creation lives there
  // (its Create CTA) and in Settings → Security. So there is no onComplete handler here.

  // s88 (Phase 3): three states from the store. In-store + dirty = amber "Unsaved";
  // in-store + clean = green "Saved in your file" (loaded from / written to the file);
  // empty (no value) = offer "Open file" to load the Secure File — but only when a file
  // exists (setupComplete); with no file yet, offer the setup pointer instead (s111).
  const inStore = session.values[storeKey] !== undefined;
  const dirty = session.getDirtyFields().includes(storeKey);
  const isUnsaved = inStore && dirty;
  const isSaved = inStore && !dirty;
  // s111 amendment + s112 SF-3: "Open file" (reveal) only makes sense when a file EXISTS on
  // the server AND hasn't been loaded into memory this session yet. Once it's loaded
  // (session.isSetupComplete), there is nothing left to open — an empty field is just an
  // editable input — so "Open file" is suppressed to avoid the "file already open, yet asks
  // to open it" contradiction. With no file at all, an empty field points to setup instead.
  const fileExistsButNotLoaded = !!secureFileSetupAt && !session.isSetupComplete;
  const showOpenLink = !readOnly && !disabled && !localValue && fileExistsButNotLoaded;
  const showSetupLink = !readOnly && !disabled && !setupComplete;
  // DB-13 (s115, Bundle F): in the dense `table` variant the stacked "Open file" /
  // setup links + saved/unsaved badges (mt-1 block elements below the input) made the
  // Cat A cell taller than its h-8 non-Cat-A neighbours. Suppress them here so the cell
  // stays a pure h-8 input; the canonical "Open file" paths remain the SecureFileLoadBanner,
  // the Protect Your Access page, and the card/drawer variants, and the input placeholder
  // ("Open your secure file to view") still signals the file-exists-not-loaded state.
  const showStackedAffordances = variant !== "table";

  const inputRow = (
    <div>
      <div className="relative">
        <Lock
          data-testid="sensitive-lock-icon"
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4F6B51] pointer-events-none"
          aria-hidden="true"
        />
        <Input
          // s112 SF-1: ALWAYS type="text" (never "password") so browser password managers
          // have no field to autofill; masking is CSS (MASK_STYLE) when hidden.
          type="text"
          // s88: value comes from the in-memory store (via local state), NEVER the server.
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          readOnly={readOnly}
          disabled={disabled}
          data-testid={`sensitive-input-${field.id}`}
          data-masked={showValue ? "false" : "true"}
          placeholder={
            disabled
              ? "Set up your Secure File above to enter this"
              : !setupComplete
              ? "Set up your Secure File to enter this"
              : fileExistsButNotLoaded
              ? "Open your secure file to view"
              : "" /* s112 SF-3: file loaded this session → just an editable field, no open/setup prompt */
          }
          className={`${variant === "table" ? "h-8 pl-9 pr-10 text-sm" : "pl-9 pr-10 text-sm"}${disabled ? " opacity-60" : ""}`}
          style={showValue ? undefined : MASK_STYLE}
          // Anti-autofill hardening (s112 SF-1). autoComplete="off" alone is ignored by
          // browser password managers; these keep built-in + third-party managers (1Password,
          // LastPass, Bitwarden, Dashlane) from writing a saved credential into a Cat A field.
          // spellCheck off also keeps the browser spellchecker from processing sensitive values.
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          name={`oe-secure-${field.id}`}
          data-1p-ignore="true"
          data-lpignore="true"
          data-bwignore="true"
          data-form-type="other"
        />
        {!disabled && (
          <button
            type="button"
            onClick={() => setShowValue((v) => !v)}
            data-testid="sensitive-show-toggle"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            tabIndex={-1}
            aria-label={showValue ? "Hide value" : "Show value"}
          >
            {showValue ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        )}
      </div>
      {showStackedAffordances && isSaved && (
        <span
          data-testid={`sensitive-saved-badge-${field.id}`}
          className="mt-1 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium"
          style={{ backgroundColor: "#EAF1E6", color: SAGE[600] }}
        >
          Saved in your file
        </span>
      )}
      {showStackedAffordances && isUnsaved && (
        <span
          data-testid={`sensitive-unsaved-badge-${field.id}`}
          className="mt-1 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium"
          style={{ backgroundColor: "#FBF1D9", color: "#8A6D1F" }}
        >
          Unsaved — download your Secure File to keep
        </span>
      )}
      {showStackedAffordances && showOpenLink && (
        <button
          type="button"
          onClick={openFile}
          data-testid={`sensitive-open-file-${field.id}`}
          className="mt-1 text-[11px] font-medium underline underline-offset-2 transition-colors"
          style={{ color: SAGE[600] }}
        >
          Open file
        </button>
      )}
      {/* s111 amendment: no secure file yet → point to setup (opens the signpost) instead
          of the "Open file" reveal, so the empty no-file state is consistent with the focus
          signpost rather than offering a file that doesn't exist. */}
      {showStackedAffordances && showSetupLink && (
        <button
          type="button"
          onClick={() => setOnboardingOpen(true)}
          data-testid={`sensitive-setup-link-${field.id}`}
          className="mt-1 text-[11px] font-medium underline underline-offset-2 transition-colors"
          style={{ color: SAGE[600] }}
        >
          Set up your Secure File
        </button>
      )}
    </div>
  );

  const modals = (
    <>
      {/* s111 amendment: field-focus on an unset-up secure file signposts to the Protect
          Your Access page (the setup home) instead of running the full inline create flow.
          Lazy-mounted (not just the open prop) so the lightweight dialog is absent until
          actually triggered. */}
      {onboardingOpen && (
        <SecureFileSignpost
          open={true}
          onOpenChange={setOnboardingOpen}
          fieldName={field.name}
        />
      )}
      {/* First-save milestone card */}
      {showMilestoneCard && (
        <FirstSaveMilestoneCard
          fieldName={field.name}
          onDismiss={() => setShowMilestoneCard(false)}
        />
      )}
    </>
  );

  if (variant === "table") {
    return (
      <>
        {inputRow}
        {modals}
      </>
    );
  }

  return (
    <div className="space-y-1">
      {inputRow}
      {/* Privacy message — card layout only */}
      <p className="text-xs text-muted-foreground">
        Saved only to your Secure Information File — never on our servers.
      </p>
      {modals}
    </div>
  );
}
